import {
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, useInfinitePage, useTableSort } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { NAV_ICONS } from "../../lib/navCopy";
import { ApiError } from "../../lib/apiClient";
import { useQueryFilters } from "../../lib/useQueryFilters";
import { formatDate, formatPaise, INVOICE_STATUS_LABELS, invoiceStatusColor } from "../../lib/format";

const INVOICE_STATUS_DEFAULT = "all";
const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Issued" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "cancelled", label: "Cancelled" },
];
const INVOICE_FILTER_DEFAULTS = { status: INVOICE_STATUS_DEFAULT, company: "" };
const INVOICE_FILTER_ALLOWED = { status: STATUS_TABS.map((item) => item.value) };
const INVOICE_FILTER_OPTIONS = { allowed: INVOICE_FILTER_ALLOWED };

export function AdminClientInvoicesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invoiceFilters = useQueryFilters(INVOICE_FILTER_DEFAULTS, INVOICE_FILTER_OPTIONS);
  const tab = invoiceFilters.values.status;
  const companyId = invoiceFilters.values.company || null;
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [selectedPlacements, setSelectedPlacements] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [notes, setNotes] = useState("");

  const statusFilter = tab === "all" ? undefined : tab;

  const invoicesQuery = useInfinitePage(["admin-invoices", statusFilter, companyId], (page, pageSize) =>
    adminApi.listInvoices({
      status: statusFilter,
      company_id: companyId ?? undefined,
      page,
      page_size: pageSize,
    }),
  );

  const companiesQuery = useQuery({
    queryKey: ["admin-client-companies"],
    queryFn: () => adminApi.listCompanies({ company_type: "client", page_size: 100 }),
  });

  const uninvoicedQuery = useQuery({
    queryKey: ["admin-uninvoiced", selectedCompany],
    queryFn: () => adminApi.listUninvoicedPlacements(selectedCompany!),
    enabled: Boolean(selectedCompany) && generateOpen,
  });

  const invoices = invoicesQuery.items;
  const companies = companiesQuery.data?.items;
  const uninvoiced = uninvoicedQuery.data ?? [];
  const { sorted, sortKey, direction, toggle } = useTableSort(invoices, {
    number: (row) => row.invoice_number,
    client: (row) => row.company_name,
    period: (row) => row.period_start,
    subtotal: (row) => row.subtotal_paise,
    gst: (row) => row.gst_paise,
    total: (row) => row.total_paise,
    status: (row) => row.status,
  });

  const companyOptions = useMemo(
    () => (companies ?? []).map((company) => ({ value: company.id, label: company.display_name || company.legal_name })),
    [companies],
  );

  function resetGenerate() {
    setGenerateOpen(false);
    setSelectedCompany(null);
    setSelectedPlacements([]);
    setDueDate("");
    setPeriodStart("");
    setPeriodEnd("");
    setNotes("");
  }

  const generateMutation = useMutation({
    mutationFn: () =>
      adminApi.generateInvoice({
        company_id: selectedCompany!,
        placement_ids: selectedPlacements,
        due_date: dueDate || undefined,
        period_start: periodStart || undefined,
        period_end: periodEnd || undefined,
        notes: notes || undefined,
      }),
    onSuccess: (invoice) => {
      notifications.show({ color: "green", title: "Invoice created", message: invoice.invoice_number });
      void queryClient.invalidateQueries({ queryKey: ["admin-invoices"] });
      resetGenerate();
      navigate(`/admin/finance/invoices/${invoice.id}`);
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Could not generate invoice",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  const issueMutation = useMutation({
    mutationFn: (id: string) => adminApi.issueInvoice(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Invoice issued" });
      void queryClient.invalidateQueries({ queryKey: ["admin-invoices"] });
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Issue failed",
        message: error instanceof ApiError ? error.message : "Request failed",
      });
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.invoices}
        title="Client invoices"
        subtitle="Raise GST invoices against client placements and record payments line by line."
        actions={
          <Button leftSection={<IconPlus size="var(--axio-unit-16)" />} onClick={() => setGenerateOpen(true)}>
            Generate invoice
          </Button>
        }
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && invoiceFilters.set({ status: value })}>
          <Tabs.List>
            {STATUS_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
        <Select
          placeholder="All clients"
          clearable
          searchable
          data={companyOptions}
          value={companyId}
          onChange={(value) => invoiceFilters.set({ company: value ?? "" })}
          w="var(--axio-unit-260)"
        />
      </FilterBar>

      <DataTableShell
        loading={invoicesQuery.isLoading}
        error={
          invoicesQuery.isError
            ? invoicesQuery.error instanceof Error
              ? invoicesQuery.error.message
              : "Could not load invoices"
            : undefined
        }
        onRetry={() => void invoicesQuery.refetch()}
        hasMore={invoicesQuery.hasMore}
        loadingMore={invoicesQuery.loadingMore}
        onLoadMore={invoicesQuery.loadMore}
        isEmpty={invoices.length === 0}
        empty={{
          icon: NAV_ICONS.invoices,
          title: "No invoices yet",
          description: "Generate an invoice from unbilled client placements.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="number" sortKey={sortKey} direction={direction} onSort={toggle}>
                Invoice
              </SortableTh>
              <SortableTh columnKey="client" sortKey={sortKey} direction={direction} onSort={toggle}>
                Client
              </SortableTh>
              <SortableTh columnKey="period" sortKey={sortKey} direction={direction} onSort={toggle}>
                Period
              </SortableTh>
              <SortableTh columnKey="subtotal" sortKey={sortKey} direction={direction} onSort={toggle}>
                Subtotal
              </SortableTh>
              <SortableTh columnKey="gst" sortKey={sortKey} direction={direction} onSort={toggle}>
                GST
              </SortableTh>
              <SortableTh columnKey="total" sortKey={sortKey} direction={direction} onSort={toggle}>
                Total
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((invoice) => (
              <Table.Tr key={invoice.id}>
                <Table.Td>
                  <Text fw="var(--axio-font-weight-semibold)">{invoice.invoice_number}</Text>
                  <Text size="xs" c="dimmed">
                    {invoice.paid_count}/{invoice.line_count} lines paid
                  </Text>
                </Table.Td>
                <Table.Td>{invoice.company_name}</Table.Td>
                <Table.Td>
                  {invoice.period_start || invoice.period_end
                    ? `${formatDate(invoice.period_start)} – ${formatDate(invoice.period_end)}`
                    : "—"}
                </Table.Td>
                <Table.Td>{formatPaise(invoice.subtotal_paise)}</Table.Td>
                <Table.Td>{formatPaise(invoice.gst_paise)}</Table.Td>
                <Table.Td>{formatPaise(invoice.total_paise)}</Table.Td>
                <Table.Td>
                  <Badge color={invoiceStatusColor(invoice.status)} variant="light">
                    {INVOICE_STATUS_LABELS[invoice.status] ?? invoice.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" justify="flex-end">
                    {invoice.status === "draft" && (
                      <Button
                        size="xs"
                        variant="light"
                        loading={issueMutation.isPending}
                        onClick={() => issueMutation.mutate(invoice.id)}
                      >
                        Issue
                      </Button>
                    )}
                    <Button component={Link} to={`/admin/finance/invoices/${invoice.id}`} size="xs" variant="subtle">
                      View
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      <Modal opened={generateOpen} onClose={resetGenerate} title="Generate client invoice" size="lg" centered>
        <Stack gap="md">
          <Select
            label="Client"
            placeholder="Select a client company"
            searchable
            data={companyOptions}
            value={selectedCompany}
            onChange={(value) => {
              setSelectedCompany(value);
              setSelectedPlacements([]);
            }}
          />
          {selectedCompany && (
            <Stack gap="xs">
              <Text size="sm" fw="var(--axio-font-weight-medium)">
                Unbilled placements
              </Text>
              {uninvoicedQuery.isLoading ? (
                <Text size="sm" c="dimmed">
                  Loading placements…
                </Text>
              ) : uninvoiced.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No unbilled placements for this client.
                </Text>
              ) : (
                uninvoiced.map((placement) => (
                  <Checkbox
                    key={placement.id}
                    label={`${placement.candidate_name} · ${placement.job_title} · ${formatPaise(placement.fee_paise)}`}
                    checked={selectedPlacements.includes(placement.id)}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setSelectedPlacements((current) =>
                        checked ? [...current, placement.id] : current.filter((id) => id !== placement.id),
                      );
                    }}
                  />
                ))
              )}
            </Stack>
          )}
          <Group grow>
            <TextInput
              type="date"
              label="Period start"
              value={periodStart}
              onChange={(event) => setPeriodStart(event.currentTarget.value)}
            />
            <TextInput
              type="date"
              label="Period end"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.currentTarget.value)}
            />
            <TextInput type="date" label="Due date" value={dueDate} onChange={(event) => setDueDate(event.currentTarget.value)} />
          </Group>
          <Textarea label="Notes" minRows={2} value={notes} onChange={(event) => setNotes(event.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={resetGenerate}>
              Cancel
            </Button>
            <Button
              loading={generateMutation.isPending}
              disabled={!selectedCompany || selectedPlacements.length === 0}
              onClick={() => generateMutation.mutate()}
            >
              Generate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
